import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, Area, AreaChart, Legend, ComposedChart,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { Wallet, TrendingUp, TrendingDown, Users, AlertCircle } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

/**
 * WalletAdoptionTracker
 * ---------------------
 * Daily count of distinct Bitcoin addresses *receiving* an output of at
 * least 0.01 BTC (1,000,000 satoshis). At today's price that's ~$800-$1,200
 * per output, so it filters out dust and tracks "real, non-trivial" inflows.
 *
 * Important: this is a FLOW metric (per-day inflow activity), not a STOCK
 * metric (total addresses holding ≥ 0.01 BTC). Stock-style metrics require
 * a UTXO scan that doesn't fit in Dune's free-tier 2-min execution cap; the
 * flow proxy is cheap enough to run reliably on a daily schedule and is a
 * solid directional indicator of adoption activity.
 *
 * Data: Dune Analytics SQL query (id stored in SSM as DUNE_WALLETS_QUERY_ID),
 *       refreshed daily on Dune's side, fetched via the dune-proxy Lambda.
 * Path: dune-proxy Lambda → API Gateway → useMarketData hook → here
 */
export default function WalletAdoptionTracker() {
  const { chain, refetch } = useMarketData();
  const series = chain?.walletsMin1MSats ?? [];
  const baselineSeries = chain?.totalActiveAddresses ?? [];

  // Latest baseline (Blockchain.com all-activity) — used for the headline
  // disambiguation pill so a casual reader doesn't confuse our 108K filtered
  // count with the ~400K-600K "active addresses" headline numbers from
  // Glassnode / news coverage. The two metrics measure different things on
  // purpose; showing both side-by-side makes the difference unmistakable.
  const baselineLatest = baselineSeries.length
    ? baselineSeries[baselineSeries.length - 1]
    : null;

  // ── Derived stats ─────────────────────────────────────────────────────────
  // Daily flow series — compare latest day to short-, medium-, and long-term
  // averages so day-of-week noise (weekend dips are real and ~25%) doesn't
  // swamp the headline. We use trailing-mean comparisons because:
  //   - 7-day mean smooths weekend effects
  //   - 30-day mean shows medium-term momentum
  //   - 1-year-ago value (single point) shows YoY trajectory
  const stats = useMemo(() => {
    if (!series.length) return null;
    const latest = series[series.length - 1];

    const trailingMean = (n) => {
      if (series.length < n) return null;
      const slice = series.slice(-n);
      const sum = slice.reduce((s, p) => s + p.value, 0);
      return { value: sum / slice.length, date: slice[0].date };
    };

    const findOnOrBefore = (cutoff) => {
      for (let i = series.length - 1; i >= 0; i--) {
        if (series[i].date <= cutoff) return series[i];
      }
      return null;
    };

    const mean7  = trailingMean(7);
    const mean30 = trailingMean(30);
    const yearAgo = findOnOrBefore(subDays(latest.date, 365));

    const pct = (then) => then && Number.isFinite(then.value) && then.value > 0
      ? ((latest.value - then.value) / then.value) * 100
      : null;

    return {
      latest,
      mean7,
      mean30,
      delta7d:  { from: mean7,   pct: pct(mean7),   isAverage: true  },
      delta30d: { from: mean30,  pct: pct(mean30),  isAverage: true  },
      delta1y:  { from: yearAgo, pct: pct(yearAgo), isAverage: false },
      allTimeHigh: series.reduce((max, p) => p.value > max.value ? p : max, series[0]),
    };
  }, [series]);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (chain.status === 'loading' && !series.length) {
    return <Skeleton />;
  }

  // ── Error state with no cached data ───────────────────────────────────────
  if (chain.status === 'error' && !series.length) {
    return <ErrorPanel error={chain.error} onRetry={refetch.chain} />;
  }

  // ── Live render ───────────────────────────────────────────────────────────
  // Format the chart data — both series merged on common days. The baseline
  // is rendered on a secondary right Y-axis because its magnitude is ~3-4×
  // larger than the filtered series (~400K vs ~108K), so a shared axis
  // would crush the filtered line into the bottom of the chart.
  const baselineByTs = new Map(
    baselineSeries.map(p => [p.date.getTime(), p.value])
  );
  const chartData = series.map(p => ({
    ts: p.date.getTime(),
    label: format(p.date, 'MMM yyyy'),
    value: p.value,
    baseline: baselineByTs.get(p.date.getTime()) ?? null,
  }));

  const isStale = chain.status === 'stale';

  // Detect when the Dune query has stopped refreshing. The query is
  // scheduled daily and the underlying SQL only looks at the trailing 730
  // days, so a healthy series always has a point within the last 2-3 days
  // (Dune's data is itself indexed with a small lag). If we go more than
  // ~5 days without a new point, surface a banner — likely causes are:
  // free-tier schedule expired (3-month cap), Dune executor regression,
  // or query SQL failing. Banner deep-links to the query so the user can
  // diagnose without leaving the dashboard.
  const dataAgeDays = stats
    ? Math.floor((Date.now() - stats.latest.date.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const dataIsStale = dataAgeDays != null && dataAgeDays > 5;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-orange-500/[0.04] via-slate-900/60 to-amber-500/[0.04] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-1">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-orange-400" />
              Daily Address Activity ≥ 1,000,000 Satoshis
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Distinct addresses receiving ≥ 0.01 BTC each day — a filtered subset of the
              broader "all-activity" baseline, dust and tiny outputs excluded
            </p>
          </div>
          {isStale && (
            <div className="flex items-center gap-1.5 text-amber-400 text-xs font-mono">
              <AlertCircle className="w-3.5 h-3.5" /> stale data
            </div>
          )}
        </div>

        {/* Data-age banner — fires when the Dune query has stopped refreshing
            (free-tier schedules eventually expire, or the underlying SQL may
            time out). Deep-links to the query so the user can diagnose
            without leaving the dashboard. */}
        {dataIsStale && (
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-200/90 leading-relaxed">
                <span className="font-semibold text-amber-300">
                  Data hasn't refreshed in {dataAgeDays} days —
                </span>{' '}
                check the source query at{' '}
                <a
                  href="https://dune.com/queries/7509472"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-amber-100"
                >
                  dune.com/queries/7509472
                </a>
                . Likely causes: Dune free-tier schedule expired (~3 months),
                executor timed out, or query failed. The panel will recover
                on its own once a fresh execution lands.
              </div>
            </div>
          </div>
        )}

        {/* Headline number */}
        {stats && (
          <>
            <div className="mt-5 flex items-baseline gap-3 flex-wrap">
              <div className="text-4xl md:text-5xl font-bold text-orange-400 font-mono tabular-nums">
                {Math.round(stats.latest.value).toLocaleString()}
              </div>
              <div className="text-sm text-slate-400">
                addresses on <strong>{format(stats.latest.date, 'MMM d, yyyy')}</strong>
                {stats.mean7 && (
                  <span className="text-slate-500">
                    {' '}· 7-day avg{' '}
                    <strong className="text-slate-300">
                      {Math.round(stats.mean7.value).toLocaleString()}
                    </strong>
                  </span>
                )}
              </div>
            </div>

            {/* Baseline disambiguation pill — shows the broader "all-activity"
                count from Blockchain.com so a user comparing this 108K to news
                headlines about ~600K active addresses sees the relationship
                immediately rather than assuming the dashboard is broken. */}
            {baselineLatest && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {/* Allow internal flex-wrap so the long single line collapses
                    onto two lines on phones rather than forcing horizontal
                    scroll on the whole section. */}
                <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 px-2 py-1 rounded-md border border-slate-700/60 bg-slate-900/50 text-slate-400">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="w-3 h-3 text-slate-400" />
                    All-activity baseline:{' '}
                    <strong className="text-slate-200 tabular-nums font-mono">
                      {Math.round(baselineLatest.value).toLocaleString()}
                    </strong>
                  </span>
                  <span className="text-slate-500">
                    on {format(baselineLatest.date, 'MMM d')}
                  </span>
                  <span className="text-slate-500">
                    · this metric ≈{' '}
                    <strong className="text-slate-300 tabular-nums">
                      {Math.round((stats.latest.value / baselineLatest.value) * 100)}%
                    </strong>{' '}
                    of total
                  </span>
                </span>
                <span
                  className="text-[10px] text-slate-600"
                  title="Blockchain.com counts every distinct address that appeared in any transaction (sender or receiver). Glassnode and CryptoQuant use cluster heuristics that produce different totals — there's no canonical 'active addresses' number."
                >
                  ⓘ
                </span>
              </div>
            )}
          </>
        )}

        {/* Delta cards — daily flow, so latest-vs-trailing-mean is the right
            comparison. Weekend dips of ~25% are normal so a single-day
            snapshot vs a single prior day is misleading. */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <DeltaCard label="vs 7-day avg"   data={stats.delta7d}  />
            <DeltaCard label="vs 30-day avg"  data={stats.delta30d} />
            <DeltaCard label="vs 1-year ago"  data={stats.delta1y}  />
          </div>
        )}

        {/* Chart — dual-axis: filtered ≥0.01 BTC series on the left (orange,
            with fill), broader Blockchain.com all-activity series on the
            right (muted gray line). Different magnitudes (~108K vs ~400K)
            require separate scales; co-plotting on a shared axis would
            crush the filtered line into the bottom of the chart. */}
        <div className="h-72 mt-6">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="walletGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#f7931a" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f7931a" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <YAxis
                yAxisId="filtered"
                orientation="left"
                tick={{ fill: '#fdba74', fontSize: 11 }}
                tickLine={false}
                width={56}
                tickFormatter={fmtCount}
              />
              <YAxis
                yAxisId="baseline"
                orientation="right"
                tick={{ fill: 'rgba(148,163,184,0.7)', fontSize: 11 }}
                tickLine={false}
                width={56}
                tickFormatter={fmtCount}
              />
              {stats?.allTimeHigh && (
                <ReferenceLine
                  yAxisId="filtered"
                  y={stats.allTimeHigh.value}
                  stroke="rgba(255,255,255,0.2)"
                  strokeDasharray="4 4"
                  label={{
                    value: `ATH ${Math.round(stats.allTimeHigh.value).toLocaleString()}`,
                    fill: 'rgba(255,255,255,0.6)',
                    fontSize: 10,
                    position: 'insideTopLeft',
                  }}
                />
              )}
              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                contentStyle={{
                  background: 'rgba(20,22,26,0.96)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#e8eaec' }}
                // Tooltip uses the row's `ts` (epoch ms) for full-date precision —
                // the X-axis ticks ("MMM yyyy") would alias many days into one label.
                labelFormatter={(_label, payload) => {
                  const ts = payload?.[0]?.payload?.ts;
                  return ts ? format(new Date(ts), 'EEE, MMM d, yyyy') : '';
                }}
                formatter={(v, name) => [
                  v != null ? `${Math.round(v).toLocaleString()} addresses` : '—',
                  name,
                ]}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#cbd5e1', paddingTop: 4 }}
                iconType="plainline"
                verticalAlign="top"
                height={28}
              />
              <Area
                yAxisId="filtered"
                type="monotone"
                dataKey="value"
                name="Filtered (≥ 0.01 BTC)"
                stroke="#f7931a"
                strokeWidth={2}
                fill="url(#walletGradient)"
                isAnimationActive={false}
              />
              {baselineSeries.length > 0 && (
                <Line
                  yAxisId="baseline"
                  type="monotone"
                  dataKey="baseline"
                  name="All-activity baseline"
                  stroke="rgba(148,163,184,0.7)"
                  strokeWidth={1.25}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <p className="text-[11px] text-slate-500 mt-3 italic leading-relaxed">
          <strong className="text-slate-400 not-italic">Filtered (orange):</strong>{' '}
          Dune Analytics SQL against bitcoin.outputs — distinct addresses receiving
          ≥ 0.01 BTC each day, trailing 730 days, refreshed daily.
          <strong className="text-slate-400 not-italic"> All-activity baseline (gray):</strong>{' '}
          Blockchain.com Charts API — total unique addresses appearing in any
          transaction (sender or receiver) per day. Different methodologies
          produce different totals: Glassnode and CryptoQuant use cluster
          heuristics that can put the "active addresses" headline anywhere
          between 400K and 800K depending on the day, so there's no single
          canonical number.
          {' '}This is a flow metric (per-day activity), not a stock metric.
          Weekend dips of ~25% are normal — compare against the trailing 7-day
          average rather than day-over-day.
        </p>
      </div>
    </section>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────
// Compact count formatter — used by both Y-axes so they tick consistently
function fmtCount(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

function DeltaCard({ label, data }) {
  if (!data?.from || data.pct == null) {
    return (
      <div className="px-4 py-3 bg-slate-900/40 border border-white/10 rounded-lg">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-lg font-mono text-slate-500 mt-1">—</div>
      </div>
    );
  }
  const positive = data.pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div className="px-4 py-3 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-mono font-bold mt-1 flex items-center gap-1.5 tabular-nums ${
        positive ? 'text-emerald-400' : 'text-red-400'
      }`}>
        <Icon className="w-4 h-4" />
        {positive ? '+' : ''}{data.pct.toFixed(2)}%
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">
        {data.isAverage ? 'baseline' : `from ${format(data.from.date, 'MMM d, yyyy')}`}
        {' '}— {Math.round(data.from.value).toLocaleString()}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 rounded bg-slate-800" />
        <div className="h-5 w-72 rounded bg-slate-800" />
      </div>
      <div className="h-12 w-64 rounded bg-slate-800 mt-4" />
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="h-16 rounded bg-slate-800" />
        <div className="h-16 rounded bg-slate-800" />
        <div className="h-16 rounded bg-slate-800" />
      </div>
      <div className="h-72 rounded bg-slate-800 mt-6" />
    </section>
  );
}

function ErrorPanel({ error, onRetry }) {
  return (
    <section className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-red-300">
            On-chain data unavailable
          </h3>
          <p className="text-sm text-slate-300 mt-1">
            We couldn't reach the on-chain proxy. The wallet adoption panel
            relies on a Dune Analytics community query — both
            <code className="mx-1 text-slate-400">DUNE_API_KEY</code> and
            <code className="mx-1 text-slate-400">DUNE_WALLETS_QUERY_ID</code>
            must be configured server-side.
          </p>
          {error && (
            <p className="mt-2 text-xs font-mono text-red-300/70 break-all">{error}</p>
          )}
          <button
            onClick={onRetry}
            className="mt-3 text-xs px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    </section>
  );
}
